import { DatePicker, Select, Spin } from 'antd';
import NumericInputField from 'components/common/NumericInputField';
import {
  PLAN_AREA_REQUIRED_MESSAGE,
  PLAN_CROP_REQUIRED_MESSAGE,
  PLAN_START_DATE_REQUIRED_MESSAGE,
} from 'constants/messageConstants';
import React, { useEffect, useMemo, useState } from 'react';
import { GcVariety, GUnit, MergedCrop } from 'interfaces/IGlobalCatalogue';
import { IInput } from 'interfaces/IInput';
import { useCalendarDayValue } from 'hooks/useCalendarDayValue';
import { ADD_RECORD_TITLES, ADD_VARIETY_LABELS } from 'constants/uiConstants';
import { useDatePickerPanel } from 'hooks/useDatePickerPanel';
import {
  END_DATE_BEFORE_START_MESSAGE,
  isEndDateBeforeStartDate,
} from 'utils/inputDraftPatches';
import MergeClassNames from 'utils/MergeClassNames';
import { AREA_UNIT_FILTER, filterGUnitsByFilter } from 'utils/GUnitFilters';

import { buildAddedMergedCrop } from './Tasks/addRecord';
import AddRecordForm from './Tasks/AddRecordForm';
import AddRecordRow from './Tasks/AddRecordRow';
import AddVarietyForm from './Tasks/AddVarietyForm';
import InlineAddPanel from './Tasks/InlineAddPanel';
import type { StepSelectorOption } from './Tasks/stepConfig';

/** The plan's selected crop as a picker option, which the variety form links a new variety to. */
const toCropOption = (crop: MergedCrop): StepSelectorOption => ({
  label: crop.name,
  item: crop.name,
  globalId: crop.globalId,
  varieties: crop.varieties,
  rate: crop.rate ?? null,
  cost: crop.cost ?? null,
  unit: crop.rateUnit ?? null,
});

interface InputAttributesFormProps {
  draft: IInput;
  selectedCropOption?: MergedCrop;
  varietyOptions: GcVariety[];
  unitValue?: string;
  mergedCrops: MergedCrop[];
  gUnits: GUnit[];
  isCropsLoading: boolean;
  isCropsPaginating: boolean;
  hasNextCropPage: boolean;
  cropSearchTerm: string;
  onLoadMoreCrops: () => void;
  onCropChange: (value: string) => void;
  onCropSearch: (value: string) => void;
  /** Cleared (undefined) removes the variety. */
  onVarietyChange: (value: string | undefined) => void;
  onStartDateChange: (value: Date | null) => void;
  onEndDateChange: (value: Date | null) => void;
  onZoneAreaChange: (value: number | null) => void;
  onUnitChange: (value: string) => void;
  /**
   * Offers "Add '<text>'" in the crop and variety dropdowns when a search finds
   * nothing, with the form shown inline under them (OP-2498).
   */
  enableAddRecord?: boolean;
  /** Called with a crop just added, to select it on the plan. */
  onCropAdded?: (crop: MergedCrop) => void;
  /** Called with a variety just added to the selected crop, to select it. */
  onVarietyAdded?: (variety: GcVariety) => void;
  // Overrides the default workspace framing (used by the create-plan modal).
  className?: string;
}

const InputAttributesForm: React.FC<InputAttributesFormProps> = ({
  draft,
  selectedCropOption,
  varietyOptions,
  unitValue,
  mergedCrops,
  gUnits,
  isCropsLoading,
  isCropsPaginating,
  hasNextCropPage,
  cropSearchTerm,
  onLoadMoreCrops,
  onCropChange,
  onCropSearch,
  onVarietyChange,
  onStartDateChange,
  onEndDateChange,
  onZoneAreaChange,
  onUnitChange,
  enableAddRecord = false,
  onCropAdded,
  onVarietyAdded,
  className,
}) => {
  const dateFormat = 'MMM D, YYYY';
  const [touched, setTouched] = useState({
    crop: false,
    startDate: false,
    area: false,
  });

  // The inline Add form under the crop / variety fields, with the text it was opened from.
  const [addPanel, setAddPanel] = useState<{
    kind: 'crop' | 'variety';
    name: string;
  } | null>(null);
  const [varietySearch, setVarietySearch] = useState('');
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [isVarietyOpen, setIsVarietyOpen] = useState(false);

  // An Add variety form belongs to the crop it was opened for.
  const selectedCropId = selectedCropOption?.globalId;
  useEffect(() => {
    setAddPanel((current) => (current?.kind === 'variety' ? null : current));
  }, [selectedCropId]);

  const errors = useMemo(() => {
    const cropError =
      touched.crop && !draft.crop?.globalId && !draft.crop?.cropName
        ? PLAN_CROP_REQUIRED_MESSAGE
        : '';
    const startDateError =
      touched.startDate && !draft.startDate
        ? PLAN_START_DATE_REQUIRED_MESSAGE
        : '';
    const areaError =
      touched.area && (!draft.zone || Number.isNaN(draft.zone.zoneArea))
        ? PLAN_AREA_REQUIRED_MESSAGE
        : '';
    // Cross-field, so it is not gated on `touched` — the user has explicitly
    // picked both dates by the time this can fail.
    const endDateError = isEndDateBeforeStartDate(
      draft.startDate,
      draft.endDate
    )
      ? END_DATE_BEFORE_START_MESSAGE
      : '';

    return {
      cropError,
      startDateError,
      areaError,
      endDateError,
    };
  }, [
    draft.crop?.globalId,
    draft.crop?.cropName,
    draft.startDate,
    draft.endDate,
    draft.zone?.zoneArea,
    touched,
  ]);

  const cropOptions = useMemo(
    () =>
      mergedCrops.map((option) => ({
        value: option.globalId,
        label: option.name,
      })),
    [mergedCrops]
  );

  const varietySelectOptions = useMemo(
    () =>
      varietyOptions.map((option) => ({
        value: option.varietyId,
        label: option.varietyName,
      })),
    [varietyOptions]
  );

  const unitOptions = useMemo(
    () =>
      filterGUnitsByFilter(gUnits, AREA_UNIT_FILTER).map((option) => ({
        value: option.globalId,
        label: option.code,
      })),
    [gUnits]
  );

  // Stable picker identities, so rc-picker does not re-run its panel sync on
  // every render — see useCalendarDayValue (OP-2285).
  const startDateValue = useCalendarDayValue(draft.startDate);
  const endDateValue = useCalendarDayValue(draft.endDate);

  // Own the open panel month so rc-picker cannot reset it — this is what makes
  // navigating to another month stick (OP-2285).
  const startDatePanel = useDatePickerPanel(startDateValue);
  const endDatePanel = useDatePickerPanel(endDateValue);

  const handleCropPopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 20 && hasNextCropPage) {
      onLoadMoreCrops();
    }
  };

  return (
    <div
      className={MergeClassNames('w-full max-w-2xl border-b-2 pb-4', className)}
    >
      <div className="grid gap-4 md:grid-cols-4">
        <div className="md:col-span-2">
          <label
            htmlFor="crop-type"
            className="mb-1 block text-sm font-semibold text-gray-dark"
          >
            Crop type <span className="text-danger">*</span>
          </label>
          <Select
            id="crop-type"
            className="w-full"
            placeholder="Select crop type"
            value={selectedCropOption?.globalId}
            onChange={(value) => {
              onCropChange(value);
              setTouched((prev) =>
                prev.crop ? prev : { ...prev, crop: true }
              );
            }}
            onBlur={() =>
              setTouched((prev) => (prev.crop ? prev : { ...prev, crop: true }))
            }
            status={errors.cropError ? 'error' : undefined}
            options={cropOptions}
            showSearch
            filterOption={false}
            searchValue={cropSearchTerm}
            onSearch={onCropSearch}
            loading={isCropsLoading}
            open={isCropOpen}
            onOpenChange={setIsCropOpen}
            notFoundContent={
              isCropsLoading ? (
                <div className="flex items-center justify-center py-[2.09375rem]">
                  <Spin size="small" />
                </div>
              ) : enableAddRecord && cropSearchTerm.trim() ? (
                <AddRecordRow
                  activateOnMouseDown
                  searchText={cropSearchTerm}
                  onClick={() => {
                    setIsCropOpen(false);
                    setAddPanel({ kind: 'crop', name: cropSearchTerm });
                  }}
                />
              ) : undefined
            }
            popupRender={(menu) => (
              <>
                {menu}
                {isCropsPaginating && (
                  <div className="flex justify-center pb-2 pt-1">
                    <Spin size="small" />
                  </div>
                )}
              </>
            )}
            onPopupScroll={handleCropPopupScroll}
          />
          {errors.cropError && (
            <p className="mt-1 text-xs text-danger">{errors.cropError}</p>
          )}
        </div>

        <div className="md:col-span-2">
          <label
            htmlFor="variety"
            className="mb-1 block text-sm font-semibold text-gray-dark"
          >
            Variety
          </label>
          <Select
            id="variety"
            className="w-full"
            placeholder="Select variety"
            value={
              varietySelectOptions.some(
                (o) => o.value === draft.crop?.variety?.globalId
              )
                ? (draft.crop?.variety?.globalId ?? undefined)
                : undefined
            }
            onChange={onVarietyChange}
            options={varietySelectOptions}
            allowClear
            disabled={!selectedCropOption}
            loading={isCropsLoading}
            open={isVarietyOpen}
            onOpenChange={(open) => {
              setIsVarietyOpen(open);
              if (!open) setVarietySearch('');
            }}
            onSearch={setVarietySearch}
            notFoundContent={
              enableAddRecord && selectedCropOption && varietySearch.trim() ? (
                <AddRecordRow
                  activateOnMouseDown
                  searchText={varietySearch}
                  onClick={() => {
                    const name = varietySearch;
                    setIsVarietyOpen(false);
                    setVarietySearch('');
                    setAddPanel({ kind: 'variety', name });
                  }}
                />
              ) : undefined
            }
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>

        {addPanel?.kind === 'crop' && (
          <div className="md:col-span-4">
            <InlineAddPanel
              title={ADD_RECORD_TITLES.crop}
              onClose={() => setAddPanel(null)}
            >
              <AddRecordForm
                kind="crop"
                initialName={addPanel.name}
                gUnits={gUnits}
                onCreated={(option, values) => {
                  setAddPanel(null);
                  if (option.globalId) {
                    onCropAdded?.(buildAddedMergedCrop(option.globalId, values));
                  }
                }}
              />
            </InlineAddPanel>
          </div>
        )}
        {addPanel?.kind === 'variety' && selectedCropOption && (
          <div className="md:col-span-4">
            <InlineAddPanel
              title={`${ADD_VARIETY_LABELS.title}: ${selectedCropOption.name}`}
              onClose={() => setAddPanel(null)}
            >
              <AddVarietyForm
                crop={toCropOption(selectedCropOption)}
                initialName={addPanel.name}
                onCreated={(_selection, variety) => {
                  setAddPanel(null);
                  onVarietyAdded?.(variety);
                }}
              />
            </InlineAddPanel>
          </div>
        )}

        <div>
          <label
            htmlFor="start-date"
            className="mb-1 block text-sm font-semibold text-gray-dark"
          >
            Start Date <span className="text-danger">*</span>
          </label>
          <DatePicker
            id="start-date"
            className="w-full"
            {...startDatePanel}
            value={startDateValue}
            onChange={(value) => {
              onStartDateChange(value ? value.toDate() : null);
              setTouched((prev) =>
                prev.startDate ? prev : { ...prev, startDate: true }
              );
            }}
            onBlur={() =>
              setTouched((prev) =>
                prev.startDate ? prev : { ...prev, startDate: true }
              )
            }
            allowClear
            format={dateFormat}
            inputReadOnly={false}
            suffixIcon={null}
            status={errors.startDateError ? 'error' : undefined}
          />
          {errors.startDateError && (
            <p className="mt-1 text-xs text-danger">{errors.startDateError}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="end-date"
            className="mb-1 block text-sm font-semibold text-gray-dark"
          >
            End Date
          </label>
          <DatePicker
            id="end-date"
            className="w-full"
            {...endDatePanel}
            value={endDateValue}
            onChange={(value) => onEndDateChange(value ? value.toDate() : null)}
            allowClear
            format={dateFormat}
            inputReadOnly={false}
            suffixIcon={null}
            status={errors.endDateError ? 'error' : undefined}
          />
          {errors.endDateError && (
            <p className="mt-1 text-xs text-danger">{errors.endDateError}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="area"
            className="mb-1 block text-sm font-semibold text-gray-dark"
          >
            Area <span className="text-danger">*</span>
          </label>
          <NumericInputField
            id="area"
            size="middle"
            inputClassName="w-full"
            min={0}
            precision={2}
            value={
              draft.zone && !Number.isNaN(draft.zone.zoneArea)
                ? draft.zone.zoneArea
                : null
            }
            onChange={(value) => {
              onZoneAreaChange(value);
              setTouched((prev) =>
                prev.area ? prev : { ...prev, area: true }
              );
            }}
            onBlur={() =>
              setTouched((prev) => (prev.area ? prev : { ...prev, area: true }))
            }
            status={errors.areaError ? 'error' : undefined}
          />
          {errors.areaError && (
            <p className="mt-1 text-xs text-danger">{errors.areaError}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="unit"
            className="mb-1 block text-sm font-semibold text-gray-dark"
          >
            Unit
          </label>
          <Select
            id="unit"
            className="w-full"
            placeholder="Select unit"
            value={unitValue}
            onChange={onUnitChange}
            options={unitOptions}
          />
        </div>
      </div>
    </div>
  );
};

export default InputAttributesForm;
